'use client';

import {
  Users,
  Calendar,
  ArrowRight,
  Link as LinkIcon,
  ShieldCheck,
  Lock,
  CreditCard,
  Check,
  Clock,
  Tag,
} from 'lucide-react';
import Link from 'next/link';
import { format } from 'date-fns/format';
import { GroupBuyCardProps } from '@/components/types/group-buy';
import { STATUS_STYLES, STATUS_TEXT } from '@/components/consts/group-buy';
import { cn } from '@/lib/utils';

export default function GroupBuyCard({ groupBuy, currentUserId }: GroupBuyCardProps) {
  const progress = (groupBuy.currentQuantity / groupBuy.targetQuantity) * 100;
  const isOwner = groupBuy.userId === currentUserId;
  const daysRemaining = Math.ceil(
    (new Date(groupBuy.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
  );
  const formattedEndDate = format(new Date(groupBuy.endDate), 'MMM d, yyyy');

  const getButtonText = () => {
    if (groupBuy.status === 'completed') {
      return 'View Details';
    }
    return currentUserId && isOwner ? 'View Details' : 'Join Group Buy';
  };

  return (
    <div className="card overflow-hidden flex flex-col min-h-[300px] hover:border-primary/30 transition-all hover:shadow">
      {/* Card Header */}
      <div className="p-4 sm:p-5 pb-2 sm:pb-3 border-b border-[hsl(var(--border))]">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="text-xs font-medium bg-primary/20 text-primary px-2 py-0.5 rounded-md">
            Group Buy
          </span>

          <span
            className={cn('text-xs font-medium px-2 py-0.5 rounded-md', {
              'bg-[hsl(var(--success-DEFAULT))]/20 text-[hsl(var(--success-DEFAULT))]':
                groupBuy.status === 'active',
              'bg-[hsl(var(--warning-DEFAULT))]/20 text-[hsl(var(--warning-DEFAULT))]':
                groupBuy.status === 'expired',
              'bg-[hsl(var(--destructive-DEFAULT))]/20 text-[hsl(var(--destructive-DEFAULT))]':
                groupBuy.status === 'cancelled',
              'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300':
                groupBuy.status === 'completed',
            })}
          >
            {STATUS_TEXT[groupBuy.status]}
          </span>

          {groupBuy.isPrivate && (
            <span className="text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded-md flex items-center gap-1">
              <Lock className="w-3 h-3" /> Private
            </span>
          )}
        </div>
        <h3 className="text-lg sm:text-xl font-bold text-foreground line-clamp-1">
          {groupBuy.title}
        </h3>
      </div>

      {/* Card Body */}
      <div className="p-4 sm:p-5 pt-2 sm:pt-3 flex-grow flex flex-col">
        <p className="text-sm text-muted-foreground line-clamp-2 mb-3 sm:mb-4">
          {groupBuy.description}
        </p>

        {/* Key Stats - Improved layout */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 py-2 sm:py-3 border-y border-[hsl(var(--border))] mb-3 sm:mb-4">
          <div className="flex items-center gap-2">
            <span className="bg-primary/15 p-1 rounded-md">
              <Users className="w-4 h-4 text-primary" />
            </span>
            <div>
              <p className="text-xs text-muted-foreground">Participants</p>
              <p className="text-sm sm:text-base font-semibold text-foreground">
                {groupBuy.currentQuantity}/{groupBuy.targetQuantity}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="bg-primary/15 p-1 rounded-md">
              <Clock className="w-4 h-4 text-primary" />
            </span>
            <div>
              <p className="text-xs text-muted-foreground">
                {daysRemaining <= 0 ? 'Ended' : 'Remaining'}
              </p>
              <p className="text-sm sm:text-base font-semibold text-foreground">
                {daysRemaining <= 0 ? formattedEndDate : `${daysRemaining} days`}
              </p>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
            <span>Progress</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full"
              style={{ width: `${Math.min(100, progress)}%` }}
            ></div>
          </div>
        </div>

        {/* Product Link */}
        {groupBuy.productLink && (
          <div className="mb-3 sm:mb-4">
            <a
              href={groupBuy.productLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:text-primary/80 flex items-center gap-1.5"
            >
              <LinkIcon className="w-3.5 h-3.5" />
              <span className="truncate">View Product</span>
            </a>
          </div>
        )}

        {/* Card Actions */}
        <div className="mt-auto">
          {/* Owner indicator */}
          {isOwner ? (
            <div className="w-full px-3 py-2 bg-primary/10 text-primary rounded-md text-center text-sm mb-3">
              <div className="flex items-center justify-center gap-1.5">
                <Check className="w-3.5 h-3.5" />
                <span className="font-medium">You created this group buy</span>
              </div>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3">
              {/* Trust indicators */}
              {/* <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-muted-foreground">Secure Purchase</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Tag className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-primary">£{groupBuy.price}</span>
                </div>
              </div> */}
            </div>
          )}

          {/* Action Buttons */}
          <div className="grid grid-cols-1 gap-2 mt-3">
            <Link
              href={`/group-buys/${groupBuy._id}`}
              className={cn(
                'text-center px-3 py-2 rounded-md font-medium transition-colors text-sm flex items-center justify-center gap-2',
                isOwner
                  ? 'bg-primary/10 hover:bg-primary/20 text-primary'
                  : 'bg-primary hover:bg-primary/90 text-primary-foreground',
              )}
            >
              {getButtonText()}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
